import { useMemo, useState } from 'react';
import { RefreshCw, DollarSign, AlertTriangle } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useTransactions, useUpdateTransaction } from '@/hooks/useTelecom';
import {
  OPERATEUR_LABELS, STATUT_TRANSACTION_LABELS,
  type OperateurMobile, type StatutTransaction,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutTransaction, string> = {
  en_attente: 'ds-bd ds-bd-w',
  validee:    'ds-bd ds-bd-s',
  echec:      'ds-bd ds-bd-d',
  remboursee: 'ds-bd ds-bd-n',
};

const OPERATEUR_COLOR: Record<OperateurMobile, string> = {
  orange: '#FF6600',
  mtn:    '#FFC107',
  moov:   '#0072CE',
  wave:   '#00BFA5',
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

export const RevenusPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]       = useState<StatutTransaction | ''>('');
  const [operateur, setOperateur] = useState<OperateurMobile | ''>('');
  const [error, setError]         = useState<string | null>(null);

  // Période du mois en cours
  const now = new Date();
  const debutMois = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const finMois   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);

  const { data, isLoading, refetch } = useTransactions({
    statut: statut || undefined, operateur: operateur || undefined,
    dateFrom: debutMois, dateTo: finMois, limit: 200,
  });
  const transactions = data?.data ?? [];
  const update = useUpdateTransaction();

  const kpis = useMemo(() => {
    const validees = transactions.filter((t) => t.statut === 'validee');
    const totalRevenu = validees.reduce((s, t) => s + Number(BigInt(t.montantCents)) / 100, 0);
    const totalData   = validees.reduce((s, t) => s + (t.dataAchetMo ?? 0), 0);
    const enAttente   = transactions.filter((t) => t.statut === 'en_attente').length;
    const echecs      = transactions.filter((t) => t.statut === 'echec').length;
    return { totalRevenu, totalData, enAttente, echecs, count: transactions.length, validees: validees.length };
  }, [transactions]);

  const onValider = async (id: string, ref: string) => {
    if (!(await demander.confirmer({ titre: `Valider la transaction ${ref} ?`, message: "Le solde data de l'abonné sera crédité.", confirmer: 'Valider' }))) return;
    setError(null);
    try { await update.mutateAsync({ id, payload: { statut: 'validee' } }); }
    catch (err) { setError(extractError(err).message); }
  };

  const moisLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <>
      <Topbar
        title="Revenus Mobile Money"
        subtitle={filiale ? `${moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1)} · ${filiale.fullName}` : 'Revenus'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(kpis.totalRevenu)}</div><div className="ds-kr-lbl">Revenus mois (FCFA)</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">✅</div><div className="ds-kr-val">{kpis.validees}</div><div className="ds-kr-lbl">Transactions validées</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">📊</div><div className="ds-kr-val">{formatNumber(kpis.totalData / 1024)}</div><div className="ds-kr-lbl">Data vendue (Go)</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">⏳</div><div className="ds-kr-val">{kpis.enAttente}</div><div className="ds-kr-lbl">En attente</div></div>
          <div className="ds-kr kr-c5"><div className="ds-kr-ico">❌</div><div className="ds-kr-val">{kpis.echecs}</div><div className="ds-kr-lbl">Échecs</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutTransaction | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_TRANSACTION_LABELS) as StatutTransaction[]).map((s) => <option key={s} value={s}>{STATUT_TRANSACTION_LABELS[s]}</option>)}
          </select>
          <select className="ds-fi" value={operateur} onChange={(e) => setOperateur(e.target.value as OperateurMobile | '')} style={{ width: 'auto' }}>
            <option value="">Tous opérateurs</option>
            {(Object.keys(OPERATEUR_LABELS) as OperateurMobile[]).map((o) => <option key={o} value={o}>{OPERATEUR_LABELS[o]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Transactions du mois</div><div className="ds-table-sub">{transactions.length} transaction{transactions.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 150 }}>Référence</th>
              <th style={{ width: 130 }}>Date</th>
              <th style={{ width: 100 }}>Opérateur</th>
              <th style={{ width: 130 }}>MSISDN</th>
              <th>Hotspot</th>
              <th className="ds-am">Montant FCFA</th>
              <th className="ds-am">Data Mo</th>
              <th style={{ width: 100 }}>Statut</th>
              <th style={{ width: 90 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && transactions.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                  <DollarSign size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune transaction ce mois</p>
                </td></tr>
              )}
              {!isLoading && transactions.map((t) => (
                <tr key={t.id}>
                  <td><span className="ds-ref-code">{t.reference}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDateTime(t.dateTransaction)}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: 'white', background: OPERATEUR_COLOR[t.operateur] }}>
                      {OPERATEUR_LABELS[t.operateur]}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--font-m)', fontSize: 12 }}>{t.msisdn}</td>
                  <td style={{ fontSize: 12 }}>{t.hotspot ? `${t.hotspot.code} — ${t.hotspot.quartier ?? t.hotspot.nom}` : '—'}</td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{formatMontant(t.montantCents)}</td>
                  <td className="ds-am">{t.dataAchetMo ? formatNumber(t.dataAchetMo) : '—'}</td>
                  <td><span className={STATUT_BADGE[t.statut]}>{STATUT_TRANSACTION_LABELS[t.statut]}</span></td>
                  <td>
                    {t.statut === 'en_attente' && (
                      <button onClick={() => onValider(t.id, t.reference)} className="ds-btn ds-btn-p ds-btn-sm" disabled={update.isPending} title="Valider la transaction">
                        Valider
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
